import { useState } from 'react';
import axiosInstance from '../../interceptor/axiosInstance';
import { ApiRoutes } from '../../routes/routeConstants/apiRoutes';
import { deserialize, serialize } from 'serializr';
import { OrganisationMapPayload } from 'models/CompanySetup/organisationPayload.model';
import { removeFalsyKeys } from 'shared/utils/removeFalsyKeys';
import Notification from 'shared/components/Notification';
import { NotificationTypes } from 'enums/notificationTypes';
import ParentCompanySetupService from 'services/ParentCompanySetup/parentCompanySetup.service';
import { generatePath } from 'react-router-dom';

const OrganisationMapService = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const { fetchParentOrganization } = ParentCompanySetupService();
  const [organisationMap, setOrganisationMap] = useState<OrganisationMapPayload>();
  const [duplicateOrganisationMap, setDuplicateOrganisationMap] = useState<OrganisationMapPayload>();

  const fetchOrganisationMap = async (
    params?: { year?: number },
    isDuplicate?: boolean,
    id?: string
  ) => {
    try {
      setLoading(true);
      const url = id
        ? generatePath(ApiRoutes.ORGANISATION_MAP_SPECIFIC, { id })
        : ApiRoutes.ORGANISATION_MAP;
      const response = await axiosInstance.get(url, {
        params,
      });
      const organisationMapData = deserialize(
        OrganisationMapPayload,
        response.data.data
      );
      if (isDuplicate) {
        const removeIdKey = (data: OrganisationMapPayload) => {
          return {
            ...data,
            organizationMap: data?.organizationMap?.map(organisation => ({
              ...organisation,
              organisationYearWise:
                organisation?.organisationYearWise?.id &&
                (({ id, ...rest }) => rest)(organisation.organisationYearWise),
            })),
          };
        };
        const desiredData = removeIdKey(organisationMapData);
        setDuplicateOrganisationMap(desiredData as OrganisationMapPayload);
        return removeFalsyKeys(desiredData) as OrganisationMapPayload;
      } else {
        setOrganisationMap(organisationMapData);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const updateOrganisationMap = async (payload: OrganisationMapPayload) => {
    try {
      setLoading(true);
      // Removing totalLocations and year from organisationYearWise to send to BE
      const modifiedOrganisationList = payload.organizationMap?.map(
        organisation => {
          const { totalLocations, year, ...remainingOrganisationYearWise } =
            organisation.organisationYearWise || {};
          return {
            ...organisation,
            organisationYearWise: remainingOrganisationYearWise,
          };
        }
      );

      const serializedPayload = removeFalsyKeys(
        serialize(OrganisationMapPayload, {
          ...payload,
          organizationMap: modifiedOrganisationList,
        })
      );
      const response = await axiosInstance.put(ApiRoutes.ORGANISATION_MAP, {
        data: serializedPayload,
      });
      const organisationMapData = deserialize(
        OrganisationMapPayload,
        response.data.data
      );
      setOrganisationMap(organisationMapData);
      Notification({
        message: 'Organization map updated',
        description: '',
        type: NotificationTypes.SUCCESS,
      });
      //Fetching parentOrganisation data if organizationalBoundary is updated
      payload.organizationMap?.length &&
        payload.organizationMap[0].company?.organizationalBoundary &&
        fetchParentOrganization();
      // for showing notification if parent organization is updated 
      if(response?.data?.data.message){
        Notification({
          message: response?.data?.data.message,
          description: '',
          type: NotificationTypes.INFO,
        });
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const createOrganisationMap = async (payload: number) => {
    try {
      setLoading(true);
      await axiosInstance.post(ApiRoutes.ORGANISATION_MAP, {
        data: { year: payload },
      });
      return true;
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const deleteYear = async (year: string) => {
    try {
      setLoading(true);
      const response = await axiosInstance.delete(ApiRoutes.ORGANISATION_MAP, {
        params: { year },
      });
      Notification({
        message:
          response?.data?.message ??
          `Organization Map deleted for the year ${year}`,
        description: '',
        type: NotificationTypes.SUCCESS,
      });
      return true;
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    organisationMap,
    duplicateOrganisationMap,
    fetchOrganisationMap,
    updateOrganisationMap,
    createOrganisationMap,
    deleteYear,
  };
};

export default OrganisationMapService; 